/** Skeleton preview with click-to-play / pause / seek / Video+Skeleton mode toggle —
 * preview payload only.
 *
 * VISTOERA_MOTION_RECONSTRUCTION_VIDEO_SKELETON_REAL_SOURCE_SYNCHRONIZED_PREVIEW_V2:
 * - "Video + Skeleton" (canonical default): real source video + H36M overlay.
 *   Video clock is master (`findNearestMotionFrameByTime`).
 * - "Skeleton": 3D Motion Asset animated by fixed-fps interval.
 *
 * VISTOERA_MOTION_RECONSTRUCTION_CLICK_PLAY_PAUSE_AND_NO_AUTO_PLAY_V3:
 * - Mount / refresh / reopen → PAUSED first frame (never auto-play).
 * - Click the preview viewport → toggle play/pause (click, not pointerdown).
 * - Offscreen / hidden-tab clears playing intent so return does NOT auto-resume.
 * - Mode switch preserves timeline position; does not force play/reset to 0.
 * - Loop only after the user has initiated playback.
 */

import { PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { MotionPreviewFrame, MotionPreviewPayload } from '../motionAssetPreview'
import {
  CANONICAL_MOTION_PREVIEW_MODE,
  normalizeLegacyMotionPreviewMode,
  type MotionPreviewMode,
} from '../motionPreviewPresentation'
import {
  advancePlaybackFrameIndex,
  computeStableProjectionBounds,
  H36M_BONES,
  inferCoordinateSpace,
  projectFrameJoints,
  resolveCoordinateSpace,
} from '../motionSkeletonPreviewCore'
import {
  findNearestMotionFrameByTime,
  projectVideoAlignedFrame,
  resolveContainedMediaRect,
  type ContainedMediaRect,
} from '../motionVideoSyncCore'
import {
  CANVAS_MEDIA_DEFAULT_LOOP,
  skeletonFrameIndexForVideoTime,
  videoTimeForSkeletonFrame,
} from '../motionPlaybackInteraction'
import { NODE_MEDIA_SURFACE_CLASS } from '../nodeInteractionContract'

type Props = {
  frames: MotionPreviewFrame[]
  width?: number
  height?: number
  fps?: number
  coordinateSpace?: MotionPreviewPayload['coordinate_space']
  /**
   * @deprecated V3 — Motion Reconstruction never auto-plays on mount.
   * Kept for API compatibility; ignored when false (default). Prefer omitting.
   */
  autoPlay?: boolean
  loop?: boolean
  /** Controlled playback — when undefined, internal state is used. */
  playing?: boolean
  onPlayingChange?: (playing: boolean) => void
  frameIndex?: number
  onFrameIndexChange?: (index: number) => void
  showControls?: boolean
  /** Legacy persisted mode from graph — body modes fall back to skeleton
   * (or video_skeleton, if a source video is available). */
  legacyPreviewMode?: string | null
  /** Real source video playback URL (canonical asset resolver output) — the
   * presence of this prop is what makes "Video + Skeleton" selectable. */
  videoUrl?: string | null
  /** 2D Motion Asset preview frames (`CanvasMotionReconstruction.input_motion_asset_id`),
   * `timestamp_ms` aligned to the source video's t=0, `normalized_2d`
   * (top-left origin) — pixel-aligned to the real video frame. */
  videoAlignedFrames?: MotionPreviewFrame[]
  /** Exposed for tests — reports current frame index over time. */
  onFrameIndexTick?: (index: number) => void
  /** Exposed for tests/E2E — reports the resolved preview mode. */
  onModeResolved?: (mode: MotionPreviewMode) => void
  /** V2 — do not stop hover preview on synthetic leave during node drag. */
  isNodeDragging?: boolean
}

export default function MotionSkeletonPreview({
  frames,
  width = 280,
  height = 160,
  fps = 12,
  coordinateSpace: coordinateSpaceProp,
  autoPlay: _autoPlay = false,
  loop = CANVAS_MEDIA_DEFAULT_LOOP,
  playing: playingProp,
  onPlayingChange,
  frameIndex: frameIndexProp,
  onFrameIndexChange,
  showControls = true,
  legacyPreviewMode,
  videoUrl,
  videoAlignedFrames = [],
  onFrameIndexTick,
  onModeResolved,
  isNodeDragging = false,
}: Props) {
  void _autoPlay // V3: intentionally unused — AUTO_PLAY_ON_MOUNT = NO
  const { t } = useTranslation('canvas')
  const rootRef = useRef<HTMLDivElement | null>(null)
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [internalPlaying, setInternalPlaying] = useState(false)
  const [internalFrameIdx, setInternalFrameIdx] = useState(0)
  const [visibleInViewport, setVisibleInViewport] = useState(true)
  const [documentVisible, setDocumentVisible] = useState(
    typeof document === 'undefined' ? true : document.visibilityState === 'visible',
  )
  const timerRef = useRef<number | null>(null)
  const frameIdxRef = useRef(0)
  const hoverPreviewActiveRef = useRef(false)

  // --- Mode resolution (Video + Skeleton vs Skeleton) ---------------------
  const [modeOverride, setModeOverride] = useState<MotionPreviewMode | null>(null)
  const [videoLoadFailed, setVideoLoadFailed] = useState(false)
  const sourceVideoAvailable = Boolean(videoUrl) && !videoLoadFailed

  const defaultMode = useMemo(
    () =>
      normalizeLegacyMotionPreviewMode(legacyPreviewMode ?? CANONICAL_MOTION_PREVIEW_MODE, {
        sourceVideoAvailable,
      }),
    [legacyPreviewMode, sourceVideoAvailable],
  )
  const previewMode: MotionPreviewMode = videoLoadFailed ? 'skeleton' : modeOverride ?? defaultMode

  useEffect(() => {
    onModeResolved?.(previewMode)
  }, [previewMode, onModeResolved])

  // --- Skeleton-only (3D asset) playback state -----------------------------
  const playing = playingProp ?? internalPlaying
  const frameIdx = frameIndexProp ?? internalFrameIdx

  useEffect(() => {
    frameIdxRef.current = frameIdx
    onFrameIndexTick?.(frameIdx)
  }, [frameIdx, onFrameIndexTick])

  const resolvedSpace = useMemo(() => {
    const explicit = resolveCoordinateSpace({ coordinate_space: coordinateSpaceProp })
    return explicit === 'unknown' ? inferCoordinateSpace(frames) : explicit
  }, [coordinateSpaceProp, frames])

  const stableBounds = useMemo(
    () => computeStableProjectionBounds(frames, resolvedSpace),
    [frames, resolvedSpace],
  )

  const setPlaying = useCallback(
    (next: boolean) => {
      if (onPlayingChange) onPlayingChange(next)
      else setInternalPlaying(next)
    },
    [onPlayingChange],
  )

  const setFrameIdx = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(next, Math.max(frames.length - 1, 0)))
      if (onFrameIndexChange) onFrameIndexChange(clamped)
      else setInternalFrameIdx(clamped)
    },
    [frames.length, onFrameIndexChange],
  )

  // Reset when the source video binding changes (new node data / new asset).
  // V3: ALWAYS start paused — never auto-play on mount / refresh / reopen.
  //
  // IMPORTANT: do NOT depend on `setFrameIdx` / `setPlaying` callback identities.
  // Those recreate when `frames.length` changes (3D preview arrives after the
  // video URL), and re-running this effect would wipe a user who already
  // clicked Play — observed on Preview as: click → briefly playing → forced
  // pause back to first frame once resultPreview frames hydrate.
  useEffect(() => {
    setFrameIdx(0)
    setModeOverride(null)
    setVideoLoadFailed(false)
    setPlaying(false)
    const video = videoRef.current
    if (video) {
      try {
        video.pause()
        if (Number.isFinite(video.currentTime) && video.currentTime !== 0) {
          video.currentTime = 0
        }
      } catch {
        /* ignore seek before metadata */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally videoUrl-only
  }, [videoUrl])

  // Pause when tab hidden or node off-screen.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const onVisibility = () => setDocumentVisible(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  useEffect(() => {
    const node = rootRef.current
    if (!node || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleInViewport(entries.some((entry) => entry.isIntersecting))
      },
      { threshold: 0.05 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  // V3 OFFSCREEN_RETURN_AUTO_RESUME = NO — clear user playing intent only on
  // a visible→hidden transition (not on every observer tick), so returning
  // does not resume and transient intersection flaps during click/focus do
  // not cancel an in-progress play.
  const wasInteractivelyVisibleRef = useRef(true)
  useEffect(() => {
    const nowVisible = visibleInViewport && documentVisible
    if (wasInteractivelyVisibleRef.current && !nowVisible) {
      setPlaying(false)
    }
    wasInteractivelyVisibleRef.current = nowVisible
  }, [visibleInViewport, documentVisible, setPlaying])

  const hasMultiFrame = previewMode === 'video_skeleton' ? Boolean(videoUrl) : frames.length > 1
  const effectivePlaying = playing && visibleInViewport && documentVisible && hasMultiFrame

  // Skeleton-only playback driver — interval timer over the 3D asset. Only
  // active in `skeleton` mode (video_skeleton uses the video clock instead).
  useEffect(() => {
    if (previewMode !== 'skeleton' || !effectivePlaying) {
      if (timerRef.current != null) window.clearInterval(timerRef.current)
      timerRef.current = null
      return
    }
    timerRef.current = window.setInterval(() => {
      const next = advancePlaybackFrameIndex(frameIdxRef.current, frames.length, loop)
      if (next == null) {
        setPlaying(false)
        if (!loop) setFrameIdx(0)
        return
      }
      setFrameIdx(next)
    }, 1000 / fps)
    return () => {
      if (timerRef.current != null) window.clearInterval(timerRef.current)
    }
  }, [previewMode, effectivePlaying, fps, frames.length, loop, setFrameIdx, setPlaying])

  // --- Video + Skeleton: contained media rect (object-fit: contain math) --
  const [containerSize, setContainerSize] = useState({ width, height })
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null)

  useEffect(() => {
    const node = viewportRef.current
    if (!node || typeof ResizeObserver === 'undefined') {
      setContainerSize({ width, height })
      return
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const box = entry.contentRect
      if (box.width > 0 && box.height > 0) setContainerSize({ width: box.width, height: box.height })
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [width, height])

  const containedRect: ContainedMediaRect = useMemo(
    () =>
      resolveContainedMediaRect(
        containerSize.width || width,
        containerSize.height || height,
        naturalSize?.width || 0,
        naturalSize?.height || 0,
      ),
    [containerSize, naturalSize, width, height],
  )

  // --- Video + Skeleton: video clock is master, resolve nearest frame -----
  const [videoFrameIdx, setVideoFrameIdx] = useState(0)
  const videoFrameIdxRef = useRef(0)
  useEffect(() => {
    videoFrameIdxRef.current = videoFrameIdx
  }, [videoFrameIdx])

  const syncFrameFromVideoTime = useCallback(
    (currentTimeSeconds: number) => {
      if (!videoAlignedFrames.length) return
      const idx = findNearestMotionFrameByTime(videoAlignedFrames, currentTimeSeconds)
      if (idx >= 0 && idx !== videoFrameIdxRef.current) {
        videoFrameIdxRef.current = idx
        setVideoFrameIdx(idx)
      }
    },
    [videoAlignedFrames],
  )

  // Mode toggle preserves timeline: sync skeleton frame ↔ video.currentTime.
  // Does not auto-play / reset to 0 / create a new job.
  const toggleMode = useCallback(() => {
    if (!videoUrl) return
    const video = videoRef.current
    if (previewMode === 'video_skeleton') {
      const t = video?.currentTime ?? 0
      if (frames.length > 0) {
        const idx = skeletonFrameIndexForVideoTime(frames, t)
        if (idx >= 0) setFrameIdx(idx)
      }
      setModeOverride('skeleton')
    } else {
      const nextT = videoTimeForSkeletonFrame(frames, frameIdx, fps)
      if (video && Number.isFinite(nextT)) {
        try {
          video.currentTime = nextT
        } catch {
          /* ignore seek before metadata */
        }
        syncFrameFromVideoTime(nextT)
      }
      setModeOverride('video_skeleton')
    }
  }, [videoUrl, previewMode, frames, frameIdx, fps, setFrameIdx, syncFrameFromVideoTime])

  // Play/pause the actual <video> element to mirror `effectivePlaying` —
  // video clock is master, no separate wall-clock timer drives it.
  //
  // Retry play on loadeddata/canplay so user-initiated play converges even
  // if buffering was incomplete when they clicked (busy multi-node canvas).
  useEffect(() => {
    const video = videoRef.current
    if (!video || previewMode !== 'video_skeleton') return
    if (!effectivePlaying) {
      video.pause()
      return
    }
    const tryPlay = () => {
      if (!video.paused) return
      video.play().catch(() => {
        /* autoplay may be rejected before user interaction — ignored; the
         * viewport click / Play control remains available. */
      })
    }
    tryPlay()
    video.addEventListener('loadeddata', tryPlay)
    video.addEventListener('canplay', tryPlay)
    return () => {
      video.removeEventListener('loadeddata', tryPlay)
      video.removeEventListener('canplay', tryPlay)
    }
  }, [previewMode, effectivePlaying])

  // requestVideoFrameCallback chain (fallback: timeupdate) — only while
  // VISIBLE + PLAYING + in video_skeleton mode. Stops on pause /
  // offscreen / hidden-tab / unmount via effect cleanup.
  // VIDEO_FRAME_CALLBACK_WHILE_PAUSED = 0
  useEffect(() => {
    const video = videoRef.current
    if (!video || previewMode !== 'video_skeleton' || !effectivePlaying) return
    let cancelled = false
    let rvfcHandle: number | null = null

    const hasRVFC = typeof (video as unknown as { requestVideoFrameCallback?: unknown }).requestVideoFrameCallback === 'function'

    if (hasRVFC) {
      const rvfcVideo = video as HTMLVideoElement & {
        requestVideoFrameCallback: (cb: (now: number, metadata: { mediaTime: number }) => void) => number
        cancelVideoFrameCallback: (handle: number) => void
      }
      const step = (_now: number, metadata: { mediaTime: number }) => {
        if (cancelled) return
        syncFrameFromVideoTime(metadata.mediaTime ?? video.currentTime)
        rvfcHandle = rvfcVideo.requestVideoFrameCallback(step)
      }
      rvfcHandle = rvfcVideo.requestVideoFrameCallback(step)
      return () => {
        cancelled = true
        if (rvfcHandle != null) rvfcVideo.cancelVideoFrameCallback(rvfcHandle)
      }
    }

    const onTimeUpdate = () => syncFrameFromVideoTime(video.currentTime)
    video.addEventListener('timeupdate', onTimeUpdate)
    return () => video.removeEventListener('timeupdate', onTimeUpdate)
  }, [previewMode, effectivePlaying, syncFrameFromVideoTime])

  // One-shot resync on pause/seek/metadata so the paused frame is always
  // correct even outside the active playback-driver effect above.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const onPauseOrSeek = () => syncFrameFromVideoTime(video.currentTime)
    video.addEventListener('pause', onPauseOrSeek)
    video.addEventListener('seeked', onPauseOrSeek)
    video.addEventListener('loadeddata', onPauseOrSeek)
    return () => {
      video.removeEventListener('pause', onPauseOrSeek)
      video.removeEventListener('seeked', onPauseOrSeek)
      video.removeEventListener('loadeddata', onPauseOrSeek)
    }
  }, [syncFrameFromVideoTime])

  // Natural end → STOP at first frame (Interaction Contract V1 §4).
  useEffect(() => {
    const video = videoRef.current
    if (!video || previewMode !== 'video_skeleton') return
    const onEnded = () => {
      hoverPreviewActiveRef.current = false
      setPlaying(false)
      try {
        if (Number.isFinite(video.currentTime) && video.currentTime !== 0) {
          video.currentTime = 0
        }
      } catch {
        /* ignore seek before metadata */
      }
      syncFrameFromVideoTime(0)
      videoFrameIdxRef.current = 0
      setVideoFrameIdx(0)
    }
    video.addEventListener('ended', onEnded)
    return () => video.removeEventListener('ended', onEnded)
  }, [previewMode, setPlaying, syncFrameFromVideoTime])

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    setNaturalSize({ width: video.videoWidth || 0, height: video.videoHeight || 0 })
  }, [])

  const handleVideoError = useCallback(() => {
    setVideoLoadFailed(true)
  }, [])

  const togglePlayback = useCallback(() => {
    if (!hasMultiFrame) return
    hoverPreviewActiveRef.current = false
    if (
      previewMode === 'skeleton' &&
      playing &&
      frameIdx >= frames.length - 1 &&
      !loop
    ) {
      setFrameIdx(0)
    }
    setPlaying(!playing)
  }, [hasMultiFrame, previewMode, playing, frameIdx, frames.length, loop, setFrameIdx, setPlaying])

  // Muted transient hover preview (video_skeleton only) — stops on leave.
  const handleViewportPointerEnter = useCallback(() => {
    if (previewMode !== 'video_skeleton' || !videoUrl || playing) return
    const video = videoRef.current
    if (!video) return
    hoverPreviewActiveRef.current = true
    video.muted = true
    void video.play().catch(() => {
      /* hover preview is best-effort */
    })
  }, [previewMode, videoUrl, playing])

  const handleViewportPointerLeave = useCallback(() => {
    if (!hoverPreviewActiveRef.current) return
    hoverPreviewActiveRef.current = false
    if (playing || isNodeDragging) return
    const video = videoRef.current
    if (!video) return
    video.pause()
    try {
      if (Number.isFinite(video.currentTime) && video.currentTime !== 0) {
        video.currentTime = 0
      }
    } catch {
      /* ignore */
    }
    syncFrameFromVideoTime(0)
    videoFrameIdxRef.current = 0
    setVideoFrameIdx(0)
  }, [playing, isNodeDragging, syncFrameFromVideoTime])

  // SVG / projection canvas must match the live viewport box (ResizeObserver),
  // not the parent prop height. Props are often NODE_WIDTH × SKELETON_HEIGHT
  // (e.g. 280×168) while the mode toggle shrinks the viewport (e.g. 280×120).
  // Using prop dims for viewBox while projecting joints into containerSize
  // (and CSS-forcing the SVG to inset:0) triggered preserveAspectRatio letterbox
  // drift vs object-fit:contain video — joints existed but could miss the body.
  const svgW = Math.max(1, Math.round(containerSize.width || width))
  const svgH = Math.max(1, Math.round(containerSize.height || height))

  // --- Render ---------------------------------------------------------------
  const points = useMemo(() => {
    if (previewMode === 'video_skeleton') {
      return projectVideoAlignedFrame(videoAlignedFrames[videoFrameIdx] || videoAlignedFrames[0], containedRect)
    }
    return projectFrameJoints({
      frame: frames[frameIdx] || frames[0],
      width: svgW,
      height: svgH,
      coordinateSpace: resolvedSpace,
      bounds: stableBounds,
    })
  }, [
    previewMode,
    videoAlignedFrames,
    videoFrameIdx,
    containedRect,
    frames,
    frameIdx,
    svgW,
    svgH,
    resolvedSpace,
    stableBounds,
  ])

  const innerBoneWidth = Math.max(2, Math.round(Math.min(svgW, svgH) * 0.012))
  const outerBoneWidth = innerBoneWidth + Math.max(2, Math.round(innerBoneWidth * 0.75))
  const innerJointRadius = Math.max(2.5, innerBoneWidth * 0.9)
  const outerJointRadius = innerJointRadius + Math.max(1.5, innerJointRadius * 0.55)

  const nothingToShow = previewMode === 'skeleton' ? points.length === 0 : !videoUrl
  if (nothingToShow) {
    return (
      <div className="pcv-motion-preview-empty" style={{ width, height }}>
        —
      </div>
    )
  }

  const singleFrame = previewMode === 'skeleton' && frames.length <= 1
  const showCenterPlay = !singleFrame && !effectivePlaying && hasMultiFrame

  return (
    <div
      ref={rootRef}
      className="pcv-motion-skeleton-player"
      style={{ width }}
      data-motion-frame-index={previewMode === 'video_skeleton' ? videoFrameIdx : frameIdx}
      data-motion-preview-mode={previewMode}
      data-motion-video-load-failed={videoLoadFailed ? 'true' : 'false'}
      data-motion-playing={effectivePlaying ? 'true' : 'false'}
      data-motion-playback-state={effectivePlaying ? 'playing' : 'paused'}
    >
      {videoUrl ? (
        <div className="pcv-motion-mode-toggle nodrag" role="group" aria-label={t('motionReconstruction.preview.modeToggle.label')}>
          <button
            type="button"
            className={`pcv-motion-mode-btn${previewMode === 'video_skeleton' ? ' is-active' : ''}`}
            data-testid="motion-mode-video-skeleton"
            onClick={(e) => {
              e.stopPropagation()
              if (previewMode !== 'video_skeleton') toggleMode()
            }}
          >
            {t('motionReconstruction.preview.modeToggle.videoSkeleton')}
          </button>
          <button
            type="button"
            className={`pcv-motion-mode-btn${previewMode === 'skeleton' ? ' is-active' : ''}`}
            data-testid="motion-mode-skeleton"
            onClick={(e) => {
              e.stopPropagation()
              if (previewMode !== 'skeleton') toggleMode()
            }}
          >
            {t('motionReconstruction.preview.modeToggle.skeleton')}
          </button>
        </div>
      ) : null}

      <div
        className={`pcv-motion-preview-viewport ${NODE_MEDIA_SURFACE_CLASS}`}
        ref={viewportRef}
        data-testid="motion-preview-viewport"
        onPointerEnter={handleViewportPointerEnter}
        onPointerLeave={handleViewportPointerLeave}
      >
        {videoUrl ? (
          <video
            ref={videoRef}
            className="pcv-motion-visual-context-layer pcv-motion-video-layer"
            style={{ display: previewMode === 'video_skeleton' ? 'block' : 'none' }}
            src={videoUrl}
            muted
            playsInline
            preload="metadata"
            loop={loop}
            onLoadedMetadata={handleLoadedMetadata}
            onError={handleVideoError}
            data-testid="motion-video-layer"
          />
        ) : (
          <div className="pcv-motion-visual-context-layer" aria-hidden="true" />
        )}
        {previewMode === 'video_skeleton' ? (
          <div className="pcv-motion-video-dark-overlay" aria-hidden="true" />
        ) : null}
        <svg
          width={svgW}
          height={svgH}
          className="pcv-motion-preview-svg pcv-motion-skeleton-overlay-layer"
          viewBox={`0 0 ${svgW} ${svgH}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {H36M_BONES.map(([a, b], i) => {
            const pa = points[a]
            const pb = points[b]
            if (!pa || !pb || pa.c < 0.15 || pb.c < 0.15) return null
            return (
              <g key={i}>
                <line
                  x1={pa.x}
                  y1={pa.y}
                  x2={pb.x}
                  y2={pb.y}
                  stroke="rgba(8, 14, 28, 0.92)"
                  strokeWidth={outerBoneWidth}
                  strokeLinecap="round"
                  opacity={0.95}
                />
                <line
                  x1={pa.x}
                  y1={pa.y}
                  x2={pb.x}
                  y2={pb.y}
                  stroke="#5EC9A6"
                  strokeWidth={innerBoneWidth}
                  strokeLinecap="round"
                  opacity={0.92}
                />
              </g>
            )
          })}
          {points.map((p, i) =>
            p.c >= 0.15 ? (
              <g key={i}>
                <circle cx={p.x} cy={p.y} r={outerJointRadius} fill="rgba(8, 14, 28, 0.92)" />
                <circle cx={p.x} cy={p.y} r={innerJointRadius} fill="#E8A94B" />
              </g>
            ) : null,
          )}
        </svg>
        {showCenterPlay ? (
          <div className="pcv-motion-center-play" aria-hidden="true" data-testid="motion-center-play">
            <PlayCircleOutlined />
          </div>
        ) : null}
      </div>

      {videoLoadFailed && videoUrl ? (
        <div className="pcv-motion-video-unavailable-hint" data-testid="motion-video-unavailable-hint">
          {t('motionReconstruction.preview.videoUnavailable')}
        </div>
      ) : null}

      {showControls && !singleFrame ? (
        <div className="pcv-motion-skeleton-controls nodrag">
          <button
            type="button"
            className="pcv-motion-skeleton-btn"
            aria-label={effectivePlaying ? t('motionReconstruction.player.pause') : t('motionReconstruction.player.play')}
            data-testid="motion-play-pause-btn"
            onClick={(e) => {
              e.stopPropagation()
              togglePlayback()
            }}
          >
            {effectivePlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
          </button>
          {previewMode === 'skeleton' ? (
            <input
              type="range"
              min={0}
              max={Math.max(frames.length - 1, 0)}
              value={frameIdx}
              className="pcv-motion-skeleton-seek"
              aria-label={t('motionReconstruction.player.seek')}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                setPlaying(false)
                setFrameIdx(Number(e.target.value))
              }}
            />
          ) : null}
        </div>
      ) : singleFrame ? (
        <div className="pcv-motion-skeleton-static-hint">{t('motionAsset.preview.singleFrame')}</div>
      ) : null}
    </div>
  )
}
